import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/Accordion/Accordion';
import { Button } from '@/components/ui/Button/Button';
import { DialogBody, DialogFooter } from '@/components/ui/Dialog/Dialog';
import { Illustration } from '@/components/ui/Illustration/Illustration';
import { toast } from '@/components/ui/Toast/Toast';
import { paths } from '@/config/paths';
import { captureError } from '@/config/sentry';
import { getStudentEnrollmentDetails } from '@/features/scheduling/utils/getStudentEnrollmentDetails';
import { useTimeFor } from '@/hooks/useTimeFor';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { useAuthStore } from '@/stores/authStore';
import type { Appointment } from '@/types/scheduling';
import { generateCalendarFile } from '@/utils/generateCalendarFile';
import { CalendarPlus, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { SessionSummary } from '../../../SessionSummary/SessionSummary';
import { useRescheduleFlow } from '../../context/useRescheduleFlow';

export const SuccessStep = () => {
  const { t } = useTranslation('scheduling');

  const { data, closeRescheduleDialog } = useRescheduleFlow();
  const { userData } = useAuthStore();
  const { formatDate } = useTimeFor('sessionTime');
  const { timeZone: calendarTimeZone } = useTimeFor('calendarExport');
  const students = userData?.students ?? [];

  const appointmentStudentConfig = data.config.data?.students.find((studentConfig) => studentConfig.id === data.appointment?.student_id);
  const appointmentEnrollment = appointmentStudentConfig?.enrollments.find((enrollment) => enrollment.id === data.appointment?.enrollment_id);

  const notBookedAppointments = data.rescheduledAppointmentDetails?.notBookedAppointments ?? data.rescheduledAppointmentDetails?.notpossible_appointments ?? [];
  const bookedAppointments = data.rescheduledAppointmentDetails?.booked_appointments ?? [];

  const hasBookedAppointments = bookedAppointments.length > 0;

  const studentConfig = data.config.data?.students;

  if (!studentConfig) {
    throw new Error('Missing student session details');
  }

  const handleAddToCalendar = () => {
    try {
      const appointment: Appointment = {
        ...data.appointment!,
        appointment_date: data.newDateAndTime || data.appointment!.appointment_date,
      };

      const student = students.find((student) => student.id === appointment.student_id);

      const title = t('addToCalendarData.sessionTitle', {
        sessionName: appointmentEnrollment?.name,
        studentFirstName: student?.firstName,
        studentLastName: student?.lastName,
      });

      const description = t('addToCalendarData.sessionDescription', {
        studentFirstName: student?.firstName,
        studentLastName: student?.lastName,
      });

      const location = userData?.center?.address ?? t('addToCalendarData.defaultLocation');

      const organizer = { name: userData?.center?.name, email: userData?.center?.email };

      void captureAnalyticsEvent('addtocalendar_clicked', {
        location: 'rescheduling_success',
        appointments_ids: bookedAppointments.map((app) => app.appointment_id),
      });
      generateCalendarFile({ appointment, students, title, description, location, organizer, notBookedAppointments, timeZone: calendarTimeZone });
    } catch (error) {
      captureError(error);

      toast({
        description: t('addToCalendarData.addToCalendarErrorToast.description'),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <DialogBody className='flex flex-1 flex-col items-center'>
        <div className='mb-6'>
          <Illustration name='workout' className='size-32' />
        </div>

        {hasBookedAppointments && (
          <>
            <h3 className='text-heading-m-semibold text-text-default-primary mb-4'>{t('rescheduleDialog.steps.successStep.title')}</h3>
            <p className='text-body-m-regular text-text-default-secondary max-w-sm'>{t('rescheduleDialog.steps.successStep.subtitle')}</p>
          </>
        )}
        {notBookedAppointments.length > 0 && (
          <Accordion type='single' collapsible className='mt-6 w-full'>
            <AccordionItem value='content' className='bg-fill-info-tile-orange text-body-m-regular w-full items-center rounded-lg px-5'>
              <AccordionTrigger>
                <div className='flex items-center gap-2'>
                  <Info size={24} className='stroke-outline-icons-orange' strokeWidth={1.5} />
                  {t('rescheduleDialog.steps.successStep.notBookedAppointments.title', { count: notBookedAppointments.length })}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <p>{t('rescheduleDialog.steps.successStep.notBookedAppointments.description')}</p>
                <ul className='mt-3'>
                  {notBookedAppointments.map((appointment) => (
                    <li key={appointment.date} className='ml-5 list-disc'>
                      <time dateTime={appointment.date}>{formatDate(appointment.date)}</time>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
        {hasBookedAppointments && (
          <div className='w-full space-y-4'>
            {bookedAppointments.map((appointment) => {
              const enrollment = getStudentEnrollmentDetails(studentConfig, appointment.student_id, appointment.enrollment_id);

              return <SessionSummary key={appointment.appointment_id} appointment={appointment} enrollment={enrollment} className='w-full' />;
            })}
          </div>
        )}
      </DialogBody>

      <DialogFooter className='flex w-full flex-col' withBorder={false}>
        {hasBookedAppointments && (
          <Button type='button' variant='text-secondary' onClick={() => void handleAddToCalendar()} className='w-full'>
            <CalendarPlus className='text-text-default-secondary size-5' />
            <span>{t('rescheduleDialog.steps.successStep.addToCalendarButton.label')}</span>
          </Button>
        )}
        <Button type='button' onClick={() => closeRescheduleDialog()} className='w-full' asChild>
          <Link to={paths.scheduling.sessions.home.path}>{t('rescheduleDialog.steps.successStep.viewSessionsButton.label')}</Link>
        </Button>
      </DialogFooter>
    </>
  );
};
